import { throttle } from 'lodash-es'
import EventBus from '@/plugins/event-bus'

/**
 * Factory function for keyboard shortcuts functionality
 * Manages product shortcuts, category navigation, and badge display
 * @param {Object} context - Vue component context (this)
 * @returns {Object} Keyboard shortcut methods
 */
export function useKeyboardShortcuts (context) {
  /**
   * Load keyboard shortcut settings from localStorage
   * Note: This is now handled by the parent component (PanelRowsWide)
   * and passed down as props to ProductGrid
   */
  function loadKeyboardSettings () {
    // Settings are loaded in parent and passed as props
    // This function is kept for backwards compatibility but does nothing
  }

  /**
   * Normalize Croatian characters to English equivalents
   * @param {string} text - Text to normalize
   * @returns {string} Normalized text
   */
  function normalizeCharacters (text) {
    if (!text) return text
    const charMap = {
      š: 's',
      Š: 'S',
      č: 'c',
      Č: 'C',
      ć: 'c',
      Ć: 'C',
      ž: 'z',
      Ž: 'Z',
      đ: 'd',
      Đ: 'D'
    }
    return text.replace(/[šŠčČćĆžŽđĐ]/g, char => charMap[char] || char)
  }

  /**
   * Generate product shortcut from label
   * @param {string} label - Product label
   * @returns {string} Generated shortcut
   */
  function generateProductShortcut (label) {
    // First normalize Croatian characters to English
    const normalizedLabel = normalizeCharacters(label)

    // Split by spaces and get first letter of first 2 words only
    const words = normalizedLabel.trim().split(/\s+/)
    let shortcut = words.slice(0, 2).map(w => w[0]).filter(Boolean).join('').toUpperCase()

    if (!shortcut) shortcut = 'A' // Fallback to 'A' if empty

    // Reserved letters (used for system buttons and actions)
    const reservedLetters = ['F', 'T', 'S', 'O', 'G', 'K']

    // Rules for replacing reserved letters:
    // 1. If shortcut starts with a digit → add 'X' prefix
    // 2. Replace ALL reserved letters with 'X' in the entire shortcut

    // First, check if starts with digit
    if (/^\d/.test(shortcut)) {
      shortcut = 'X' + shortcut
    }

    // Replace all reserved letters with 'X'
    reservedLetters.forEach(letter => {
      shortcut = shortcut.replace(new RegExp(letter, 'g'), 'X')
    })

    return shortcut
  }

  /**
   * Get active categories from the grid in flat array
   * @returns {Array} Array of active category items
   */
  function getActiveCategoriesFlat () {
    if (!context.rows) return []

    const categories = []
    for (let i = 0; i < context.rows.length; i++) {
      for (let j = 0; j < context.rows[i].cols.length; j++) {
        const item = context.rows[i].cols[j]
        if (item.active && item.type === 'category') {
          categories.push(item)
        }
      }
    }
    return categories
  }

  /**
   * Get shortcut for a specific item (category or product)
   * @param {Object} item - The item
   * @returns {string|null} The shortcut
   */
  function getItemShortcut (item) {
    if (!item.active) return null

    // Support both camelCase (rowIndex) and snake_case (row_index) property names
    const rowIndex = item.rowIndex !== undefined ? item.rowIndex : item.row_index
    const colIndex = item.colIndex !== undefined ? item.colIndex : item.col_index

    if (item.type === 'category') {
      // Categories get numbers
      const activeCategories = getActiveCategoriesFlat()
      const index = activeCategories.findIndex(cat => {
        const catRow = cat.rowIndex !== undefined ? cat.rowIndex : cat.row_index
        const catCol = cat.colIndex !== undefined ? cat.colIndex : cat.col_index
        return catRow === rowIndex && catCol === colIndex
      })
      return index >= 0 ? index + 1 : null
    } else if (item.type === 'product' || item.type === 'menu-product') {
      // Products get letter shortcuts
      const key = `${rowIndex}-${colIndex}`
      return context.productShortcutMap[key] || null
    }
    return null
  }

  /**
   * Get category shortcut (number)
   * @param {Object} item - The category item
   * @returns {number|null} The category number
   */
  function getCategoryShortcut (item) {
    if (!item.active || item.type !== 'category') return null

    // Support both camelCase and snake_case property names
    const rowIndex = item.rowIndex !== undefined ? item.rowIndex : item.row_index
    const colIndex = item.colIndex !== undefined ? item.colIndex : item.col_index

    const activeCategories = getActiveCategoriesFlat()
    const index = activeCategories.findIndex(cat => {
      const catRow = cat.rowIndex !== undefined ? cat.rowIndex : cat.row_index
      const catCol = cat.colIndex !== undefined ? cat.colIndex : cat.col_index
      return catRow === rowIndex && catCol === colIndex
    })
    return index >= 0 ? index + 1 : null
  }

  /**
   * Assign shortcuts to all products in the grid
   * Called when grid data changes
   */
  function assignProductShortcuts () {
    if (!context.rows) return

    // Clear previous shortcuts
    context.productShortcutMap = {}
    const shortcutCountMap = {}

    // Iterate through all grid items
    for (let i = 0; i < context.rows.length; i++) {
      for (let j = 0; j < context.rows[i].cols.length; j++) {
        const item = context.rows[i].cols[j]
        if (item.active && (item.type === 'product' || item.type === 'menu-product')) {
          const baseShortcut = generateProductShortcut(item.label)
          let shortcut = baseShortcut

          // Handle duplicates
          if (shortcutCountMap[baseShortcut] !== undefined) {
            shortcutCountMap[baseShortcut]++
            const count = shortcutCountMap[baseShortcut]
            if (count <= 9) {
              shortcut = `${baseShortcut}${count}`
            } else {
              shortcut = `${baseShortcut}${count}`
            }
          } else {
            shortcutCountMap[baseShortcut] = 0
          }

          item.shortcut = shortcut
          // Support both camelCase and snake_case property names
          const rowIndex = item.rowIndex !== undefined ? item.rowIndex : item.row_index
          const colIndex = item.colIndex !== undefined ? item.colIndex : item.col_index
          const key = `${rowIndex}-${colIndex}`
          context.productShortcutMap[key] = shortcut
        }
      }
    }
  }

  /**
   * Handle category/product key press
   * @param {KeyboardEvent} e - The keyboard event
   */
  function handleCategoryKeyPress (e) {
    // Ignore if keyboard shortcuts are disabled
    if (!context.keyboardShortcutsEnabled) return

    // Ignore if modal is open or in edit mode
    if (context.selectProductDialogOpen || context.editMode) return

    // Ignore if any v-dialog is open (including Floor Plan)
    const openDialogs = document.querySelectorAll('.v-dialog--active')
    if (openDialogs.length > 0) return

    // Ignore if user is typing in an input field
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return

    const key = e.key

    // Handle both digits (for categories) and letters (for products)
    if (/^[a-zA-Z0-9]$/.test(key)) {
      // Clear previous timer
      if (context.categoryKeyTimer) {
        clearTimeout(context.categoryKeyTimer)
      }

      // Add key to buffer (uppercase for letters)
      context.categoryKeyBuffer += key.toUpperCase()

      // Set timer to process buffer after 300ms
      context.categoryKeyTimer = setTimeout(() => {
        const buffer = context.categoryKeyBuffer

        // Try to match product shortcut first (letters)
        if (/^[A-Z]/.test(buffer)) {
          // Find product with matching shortcut
          for (let i = 0; i < context.rows.length; i++) {
            for (let j = 0; j < context.rows[i].cols.length; j++) {
              const item = context.rows[i].cols[j]
              if (item.active && item.shortcut === buffer) {
                context.onCellClick(item)
                break
              }
            }
          }
        } else if (/^\d+$/.test(buffer)) {
          // Try to match category number
          const num = parseInt(buffer)
          const activeCategories = getActiveCategoriesFlat()
          if (num > 0 && num <= activeCategories.length) {
            context.onCellClick(activeCategories[num - 1])
          }
        }

        // Clear buffer
        context.categoryKeyBuffer = ''
      }, 300)
    }
  }

  /**
   * Handle Space key down - show badges temporarily
   * @param {KeyboardEvent} e - The keyboard event
   */
  function handleSpaceKeyDown (e) {
    // Only process if it's the Space key
    if (e.key !== ' ') return

    // Ignore if user is typing in an input field - check BEFORE preventDefault!
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return

    // Check if shortcuts are enabled
    if (!context.keyboardShortcutsEnabled) return

    // Ignore if modal is open or in edit mode
    if (context.selectProductDialogOpen || context.editMode) return

    // Ignore if any v-dialog is open (including Floor Plan)
    const openDialogs = document.querySelectorAll('.v-dialog--active')
    if (openDialogs.length > 0) return

    // Prevent default space behavior (scrolling) - do this AFTER all checks!
    e.preventDefault()

    // If Space key is already pressed, don't start a new timer
    if (context.spaceKeyPressed) return

    // Mark that Space key is pressed
    context.spaceKeyPressed = true

    // Start timer for 200ms - quick response for showing shortcuts
    context.spaceKeyPressTimer = setTimeout(() => {
      // After 200ms, show badges temporarily
      context.showBadgesTemporarily = true
      // Emit event for sibling components (PaymentPanel)
      EventBus.$emit('show-badges-temporarily', true)
    }, 200)
  }

  /**
   * Handle Space key up - hide badges
   * @param {KeyboardEvent} e - The keyboard event
   */
  function handleSpaceKeyUp (e) {
    // Only process if it's the Space key
    if (e.key !== ' ') return

    // Ignore if user is typing in an input field - check BEFORE preventDefault!
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return

    // Prevent default space behavior (scrolling)
    e.preventDefault()

    // Clear the timer if key is released before 200ms
    if (context.spaceKeyPressTimer) {
      clearTimeout(context.spaceKeyPressTimer)
      context.spaceKeyPressTimer = null
    }

    // Hide badges and reset flag
    context.showBadgesTemporarily = false
    context.spaceKeyPressed = false
    // Emit event for sibling components (PaymentPanel)
    EventBus.$emit('show-badges-temporarily', false)
  }

  /**
   * Cleanup function for timers
   */
  function cleanup () {
    if (context.categoryKeyTimer) {
      clearTimeout(context.categoryKeyTimer)
    }
    if (context.spaceKeyPressTimer) {
      clearTimeout(context.spaceKeyPressTimer)
    }
  }

  // Throttled versions for performance
  const throttledAssignShortcuts = throttle(assignProductShortcuts, 500)
  const throttledCategoryKeyPress = throttle(handleCategoryKeyPress, 100)

  return {
    // Functions
    loadKeyboardSettings,
    normalizeCharacters,
    generateProductShortcut,
    getActiveCategoriesFlat,
    getItemShortcut,
    getCategoryShortcut,
    assignProductShortcuts,
    handleCategoryKeyPress,
    handleSpaceKeyDown,
    handleSpaceKeyUp,
    cleanup,

    // Throttled functions
    throttledAssignShortcuts,
    throttledCategoryKeyPress
  }
}
